/**
 * Índice e quadros da chuva por satélite publicados pela máquina do agendador
 * (`AmaView-dados/agendador/gsmap`).
 *
 * O GSMaP_NOW da JAXA é a taxa de chuva média de uma hora (mm/h) numa grade
 * global de 0,1°, refeita a cada 30 min, num servidor com conta (SFTP). A
 * máquina recorta o setor NSA do GOES-19 (442 × 594 pontos), grava um byte
 * por ponto (`v = (q/20)²`; 255 = sem dado) e publica as últimas 48 h com
 * CORS `*`: ~18 KB comprimido por horário.
 */
import { fetchWithRetry } from '../lib/fetchRetry';
import { HOUR_MS } from '../lib/time';
import type { Grade } from '../previsao/interp';

/** Onde a máquina publica. `VITE_GSMAP_URL` aponta para outro lugar (vazio desliga a camada). */
export const BASE_GSMAP = (import.meta.env.VITE_GSMAP_URL ?? 'https://147.15.84.134/gsmap').replace(/\/$/, '');

export const GSMAP_URL = 'https://sharaku.eorc.jaxa.jp/GSMaP_NOW/index.htm';

/** O crédito que a JAXA pede. */
export const GSMAP_ATRIBUICAO = 'Chuva: GSMaP data by JAXA';

/** Produto do horário: `g` ajustado por pluviômetros (GSMaP_Gauge_NOW), `s` só satélite (GSMaP_NOW). */
export type ProdutoGsmap = 'g' | 's';

export interface QuadroGsmap {
  /** Início da hora (epoch ms, UTC). */
  ini: number;
  /** Fim da hora (epoch ms). */
  fim: number;
  /** Meio da hora: o instante que o quadro representa no casamento com o satélite. */
  t: number;
  /** Carimbo AAAAMMDDHHNN do início (nome do arquivo). */
  c: string;
  p: ProdutoGsmap;
  /** Maior taxa no recorte (mm/h). */
  max: number;
  /** Fração dos pontos com dado que têm pelo menos 1 mm/h. */
  f1: number;
  /** Fração do recorte sem dado. */
  sem: number;
}

export interface Codificacao {
  fator: number;
  semDado: number;
}

export interface IndiceGsmap {
  /** Quando a máquina escreveu o índice (epoch ms), ou null. */
  gerado: number | null;
  grade: Grade;
  codificacao: Codificacao;
  /** Quadros em ordem crescente de tempo. */
  quadros: QuadroGsmap[];
}

/** AAAAMMDDHHNN (UTC) → epoch ms, ou null. */
export function lerCarimbo(c: string): number | null {
  const m = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})$/.exec(c);
  if (!m) return null;
  const [ano, mes, dia, hora, min] = m.slice(1).map(Number);
  if (mes < 1 || mes > 12 || dia < 1 || dia > 31 || hora > 23 || min > 59) return null;
  return Date.UTC(ano, mes - 1, dia, hora, min);
}

function numero(v: unknown, padrao = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : padrao;
}

/** Interpreta o índice; entradas inválidas são descartadas. Lança erro só quando o arquivo inteiro não serve. */
export function lerIndice(cru: unknown): IndiceGsmap {
  const m = cru as { gerado?: unknown; quadros?: unknown; grade?: Record<string, unknown>; codificacao?: Record<string, unknown>; duracao_min?: unknown };
  if (!m || typeof m !== 'object' || !Array.isArray(m.quadros)) throw new Error('índice da chuva sem a lista `quadros`');
  const g = m.grade ?? {};
  const grade: Grade = { lat0: numero(g.lat0, NaN), lon0: numero(g.lon0, NaN), passo: numero(g.passo, NaN), ni: numero(g.ni), nj: numero(g.nj) };
  if (![grade.lat0, grade.lon0, grade.passo].every(Number.isFinite) || grade.passo <= 0 || grade.ni < 2 || grade.nj < 2) {
    throw new Error('índice da chuva sem a grade');
  }
  const cod = m.codificacao ?? {};
  if (cod.tipo !== undefined && cod.tipo !== 'raiz') throw new Error(`codificação desconhecida: ${String(cod.tipo)}`);
  const codificacao: Codificacao = { fator: numero(cod.fator, 20), semDado: numero(cod.semDado, 255) };
  const duracao = numero(m.duracao_min, 60) * 60_000 || HOUR_MS;
  const vistos = new Set<number>();
  const quadros: QuadroGsmap[] = [];
  for (const q of m.quadros as Record<string, unknown>[]) {
    if (!q || typeof q.c !== 'string') continue;
    const ini = lerCarimbo(q.c);
    if (ini === null || vistos.has(ini)) continue;
    vistos.add(ini);
    quadros.push({
      ini,
      fim: ini + duracao,
      t: ini + duracao / 2,
      c: q.c,
      p: q.p === 's' ? 's' : 'g',
      max: Math.max(0, numero(q.max)),
      f1: Math.min(1, Math.max(0, numero(q.f1))),
      sem: Math.min(1, Math.max(0, numero(q.sem))),
    });
  }
  quadros.sort((a, b) => a.ini - b.ini);
  const gerado = typeof m.gerado === 'string' ? Date.parse(m.gerado) : NaN;
  return { gerado: Number.isFinite(gerado) ? gerado : null, grade, codificacao, quadros };
}

/** O índice muda a cada horário novo; `no-cache` revalida no servidor (ele responde 304 quando nada mudou). */
export async function carregarIndice(signal?: AbortSignal): Promise<IndiceGsmap> {
  const cru = await fetchWithRetry<unknown>(`${BASE_GSMAP}/v1/indice.json`, { cache: 'no-cache', mode: 'cors', credentials: 'omit' }, {
    signal,
    read: (r) => r.json() as Promise<unknown>,
  });
  return lerIndice(cru);
}

export function urlQuadro(q: Pick<QuadroGsmap, 'c' | 'p'>, base: string = BASE_GSMAP): string {
  return `${base}/v1/quadros/${q.c}-${q.p}.bin`;
}

/** Tabela byte → mm/h (NaN sem dado). */
export function tabelaDeValores(cod: Codificacao): Float32Array {
  const t = new Float32Array(256);
  for (let q = 0; q < 256; q++) t[q] = q === cod.semDado ? NaN : (q / cod.fator) ** 2;
  return t;
}

/** Baixa um quadro (imutável: o cache HTTP do navegador guarda). Os bytes crus, um por ponto. */
export async function carregarQuadro(q: QuadroGsmap, grade: Grade, signal?: AbortSignal): Promise<Uint8Array> {
  const buf = await fetchWithRetry<ArrayBuffer>(urlQuadro(q), { mode: 'cors', credentials: 'omit' }, {
    signal,
    read: (r) => r.arrayBuffer(),
  });
  const bytes = new Uint8Array(buf);
  if (bytes.length !== grade.ni * grade.nj) throw new Error(`quadro de ${bytes.length} pontos, esperado ${grade.ni * grade.nj}`);
  return bytes;
}
